import { useEffect, useState } from 'react';

import { api } from '~/api';
import type { Place } from './destinations';
import type { HotelSummary, ImageSource } from './api/types';
import { encodeOccupancy, type Occupancy } from './occupancy';

// Homepage merchandising comes from the CMS (Admin → CMS, `/cms/list`).
// This module turns CMS items into the shapes the homepage cards render and
// keeps the last answer so a returning visitor sees the sections instantly.

export type CmsSectionKey = Api.Cms.SectionKey;
export type CmsSections = Partial<Record<CmsSectionKey, Api.Cms.Section>>;

export type HomepageCmsState = {
  // 'loading' only before the first answer (cached or live) arrives;
  // 'unavailable' when the CMS could not be reached and nothing is cached.
  status: 'loading' | 'ready' | 'unavailable';
  sections: CmsSections;
};

export type CmsHotel = HotelSummary & {
  // Where a card without a bookable hotel id sends the traveller.
  destination: { label: string; place: Place | null };
  dealSearch: { checkin: string; checkout: string; occ: string; residency: string } | null;
};

export type CmsDestination = {
  key: string;
  city: string;
  image: ImageSource | null;
  place: Place | null;
};

const STORAGE_KEY = 'suitescanner-cms-v1';
let memoryCache: CmsSections | null = null;

function readStored(): CmsSections | null {
  try {
    const raw = (globalThis as { localStorage?: Storage }).localStorage?.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as CmsSections) : null;
  } catch {
    return null;
  }
}

function writeStored(sections: CmsSections): void {
  try {
    (globalThis as { localStorage?: Storage }).localStorage?.setItem(STORAGE_KEY, JSON.stringify(sections));
  } catch {
    // Private mode / quota: the in-memory copy still serves this visit.
  }
}

export function useHomepageCms(): HomepageCmsState {
  const [state, setState] = useState<HomepageCmsState>(() => {
    const cached = memoryCache ?? readStored();
    return cached ? { status: 'ready', sections: cached } : { status: 'loading', sections: {} };
  });

  useEffect(() => {
    let cancelled = false;
    api.cms
      .list()
      .then((response) => {
        if (cancelled || !response?.data) throw new Error('cms_unavailable');
        memoryCache = response.data as CmsSections;
        writeStored(memoryCache);
        setState({ status: 'ready', sections: memoryCache });
      })
      .catch(() => {
        if (!cancelled) setState((current) => (current.status === 'ready' ? current : { status: 'unavailable', sections: {} }));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}

function imageOf(url: string | undefined): ImageSource | null {
  if (!url) return null;
  return { uri: url.replace('{size}', '640x400') };
}

function cityPlace(item: Api.Cms.Item): Place | null {
  const label = item.city || item.name || '';
  if (!label || !item.regionId) return null;
  return { kind: 'city', label, sublabel: item.country ?? '', id: String(item.regionId) };
}

function money(amount: number): HotelSummary['lowestPrice'] {
  return { amount: amount.toFixed(2), currencyCode: 'USD' };
}

function dealSearchOf(item: Api.Cms.Item): CmsHotel['dealSearch'] {
  if (!item.checkIn || !item.checkOut) return null;
  const roomCount = Math.max(1, Math.trunc(item.rooms ?? 1));
  const adultCount = Math.max(1, Math.trunc(item.adults ?? 2));
  const childCount = Math.max(0, Math.trunc(item.children ?? 0));
  const adultsPerRoom = Math.floor(adultCount / roomCount);
  const occupancy: Occupancy = Array.from({ length: roomCount }, (_, index) => ({
    adults: adultsPerRoom + (index < adultCount % roomCount ? 1 : 0),
    children: index === 0 ? Array.from({ length: childCount }, () => 8) : [],
  }));
  return {
    checkin: item.checkIn,
    checkout: item.checkOut,
    occ: encodeOccupancy(occupancy),
    residency: (item.residency || 'US').toLowerCase(),
  };
}

// Hotel cards need a member price; an item without one is not shown.
export function toCmsHotel(item: Api.Cms.Item): CmsHotel | null {
  const price = Number(item.price) || 0;
  if (price <= 0 || !item.name) return null;
  const publicPrice = Number(item.guestPrice) || 0;
  const rating = Number(item.rating) || 0;

  return {
    id: item.id,
    hid: item.hid && item.hid > 0 ? item.hid : 0,
    name: item.name,
    starRating: item.starRating ?? 0,
    // The API sends the hotel's stored score on every response; show it as is.
    reviewScore: rating > 0 ? rating : null,
    reviewCount: item.reviewCount ?? null,
    thumbnail: imageOf(item.image),
    address: [item.city, item.country].filter(Boolean).join(', '),
    lowestPrice: money(price),
    retailPrice: money(publicPrice > price ? publicPrice : price),
    comparisonAvailable: publicPrice > price,
    rates: [],
    destination: { label: item.city || item.name, place: cityPlace(item) },
    dealSearch: dealSearchOf(item),
  };
}

// A destination item's display name is `name` ("Tokyo"); `city` holds the
// supplier's region ("Tokyo Prefecture").
export function toCmsDestination(item: Api.Cms.Item): CmsDestination | null {
  const city = item.name || item.city;
  if (!city) return null;
  const place = item.regionId ? { kind: 'city' as const, label: city, sublabel: item.country ?? '', id: String(item.regionId) } : null;
  return { key: item.id, city, image: imageOf(item.image), place };
}

export function cmsHotels(section: Api.Cms.Section | undefined): CmsHotel[] {
  return (section?.items ?? []).map(toCmsHotel).filter((hotel): hotel is CmsHotel => hotel !== null);
}

export function cmsDestinations(section: Api.Cms.Section | undefined): CmsDestination[] {
  return (section?.items ?? []).map(toCmsDestination).filter((item): item is CmsDestination => item !== null);
}

// Titles render as the site's split heading. An editor can mark the accented
// words with asterisks ("Today’s *SuiteScanner+* prices"); otherwise
// "SuiteScanner+" is accented, else the last word.
export function splitTitle(title: string): { pre: string; accent: string; rest: string } {
  const marked = title.match(/^(.*?)\*(.+?)\*(.*)$/);
  if (marked) return { pre: marked[1], accent: marked[2], rest: marked[3] };
  const brand = title.indexOf('SuiteScanner+');
  if (brand >= 0) {
    return { pre: title.slice(0, brand), accent: 'SuiteScanner+', rest: title.slice(brand + 'SuiteScanner+'.length) };
  }
  const lastSpace = title.trimEnd().lastIndexOf(' ');
  return lastSpace > 0
    ? { pre: title.slice(0, lastSpace + 1), accent: title.slice(lastSpace + 1).trim(), rest: '' }
    : { pre: '', accent: title, rest: '' };
}
